import { TCardFacet, TFacet } from '@/types/collection'
import { FilterValues } from '../types/filter-store-v2.types'

/**
 * Parse filters from URLSearchParams to object
 */
export const parseFiltersFromSearchParams = (searchParams: URLSearchParams) => {
  const initial: Record<string, string | string[]> = {}
  searchParams.forEach((value, key) => {
    initial[key] = value.includes(',') ? value.split(',') : value
  })
  return initial
}

/**
 * Lấy giá trị filter dạng string (ưu tiên cho radio, color, ...)
 */
export const getFilterValue = (
  key: string,
  filter: Record<string, string | string[] | undefined>
): string | undefined => {
  const val = filter[key]
  return typeof val === 'string' ? val : undefined
}

/**
 * Lấy giá trị filter dạng array (ưu tiên cho checkbox, multi-select)
 */
export const getFilterValues = (
  key: string,
  filter: Record<string, string | string[] | undefined>
): string[] => {
  const val = filter[key]
  if (!val) return []
  return Array.isArray(val) ? val : [val.toString()]
}

/**
 * Lấy danh sách key filter hợp lệ từ facets (collection structure)
 */
export function getValidFilterKeys(facets: TFacet[]): string[] {
  const normalAttributes = facets || []
  return [
    'size',
    'color',
    'price_range',
    'kieu-dang-quan-lot', // Giữ lại cho trường hợp đặc biệt
    'gender_type',
    ...normalAttributes.map((attribute: TFacet) => attribute.seo_alias),
  ]
}

/**
 * Lấy danh sách key filter hợp lệ từ facets mới (TFacet[], TCardFacet[])
 */
export function getValidFilterKeysFromFacets(
  facets?: TFacet[],
  cardFacets?: TCardFacet[]
): string[] {
  const normalAttributes = facets || []
  const attributeCards = cardFacets || []
  return [
    'size',
    'color',
    'price_range',
    'kieu-dang-quan-lot', // Giữ lại cho trường hợp đặc biệt
    'gender_type',
    ...normalAttributes.map((attribute: TFacet) => attribute.seo_alias),
    ...attributeCards.map((card: TCardFacet) => card.seo_alias),
  ]
}

/**
 * Trả về trạng thái filter: có filter không và số lượng filter đang active
 */
export function getFilterStatus(
  filter: Record<string, string | string[] | undefined>,
  facets: TFacet[]
) {
  if (!facets) {
    return {
      hasFilter: false,
      filterCount: 0,
    }
  }

  const filterValid = getValidFilterKeys(facets)
  let count = 0
  let hasActiveFilter = false

  for (const key of Object.keys(filter)) {
    if (filterValid.includes(key) && filter[key] && filter[key]?.length !== 0) {
      hasActiveFilter = true
      // Đếm theo số lượng giá trị được chọn cho các filter dạng mảng
      if (Array.isArray(filter[key])) {
        count += (filter[key] as string[]).length
      } else {
        count++
      }
    }
  }

  return {
    hasFilter: hasActiveFilter,
    filterCount: count,
  }
}

/**
 * Trả về trạng thái filter với facets mới (TFacet[], TCardFacet[])
 */
export function getFilterStatusFromFacets(
  filter: Record<string, string | string[] | number | undefined>,
  facets?: TFacet[],
  cardFacets?: TCardFacet[]
) {
  if (!facets && !cardFacets) {
    return {
      hasFilter: false,
      filterCount: 0,
    }
  }

  const filterValid = getValidFilterKeysFromFacets(facets, cardFacets)
  let count = 0
  let hasActiveFilter = false

  for (const key of Object.keys(filter)) {
    const value = filter[key]
    if (
      filterValid.includes(key) &&
      value !== undefined &&
      value !== null &&
      value !== ''
    ) {
      hasActiveFilter = true
      // Đếm theo số lượng giá trị được chọn cho các filter dạng mảng
      if (Array.isArray(value)) {
        count += value.length
      } else {
        count++
      }
    }
  }

  return {
    hasFilter: hasActiveFilter,
    filterCount: count,
  }
}

interface UpdateUrlOptions {
  scroll?: boolean
  shallow?: boolean
}

/**
 * Check if should add rel="nofollow" to filter links
 * Returns true if there are 2 or more active filter values
 */
export function shouldAddNofollowToFilterLinks(
  filters: FilterValues,
  facets?: TFacet[],
  cardFacets?: TCardFacet[]
): boolean {
  const { filterCount } = getFilterStatusFromFacets(filters, facets, cardFacets)
  return filterCount >= 2
}

/**
 * Update URL using history API with options
 * @deprecated Use updateUrl from url-utils.ts instead
 */
export const updateUrl = (url: string, options?: UpdateUrlOptions) => {
  // Fallback cho trường hợp không có router (ví dụ: trong tests)
  window.history.pushState({}, '', url)
  if (options?.scroll === false) {
    window.scrollTo(0, window.scrollY)
  }
}
